// ═══════════════════════════════════════════════════════════════════
// BodiesStage.js — SHIP 8 · THE REVIEW ROOM
// ═══════════════════════════════════════════════════════════════════
// ie-being-models §4b: "Building a body is only half the batch; the
// other half is the room you look at it in." This is that room for the
// Shatterfield's standing beings — every Escendant card in the roster,
// standing on a card slab exactly as <FieldBeing> stands it on a rift,
// under the SAME light rig the duel uses, with an orbit camera.
//
// Open it at  http://localhost:3013/?stage=bodies  (index.js routes it).
// Dev-only in spirit; it touches no game state and no Firebase. Zero
// game logic, zero imports from the duel beyond the adapter itself.
// ═══════════════════════════════════════════════════════════════════
import { useEffect, useMemo, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import { ESCENDANTS } from "./cards";
import { CARD } from "./fieldLayout3D";
import { FieldBeing, beingOf, STAND_H } from "./fieldBeings3D";
import Shatterfield3D from "./Shatterfield3D";
// SHIP 9 — the pill is reviewed here too, over the fake duel, with a
// stand-in stampsFor (every seal "ready") and no gateCtx (the codex builds
// its own from me/opponent/turn). Real gates, fake state, no Firebase.
import FieldPill from "./FieldPill";
import { PRIMERAS } from "./rules";
import { baseId } from "./necroSeal";

// ── THE FIELD STAGE — `?stage=field` ─────────────────────────────
// The REAL 3D board (Shatterfield3D is a pure renderer: same data in,
// same callbacks out) fed a fabricated duel state, so a rift full of
// beings can be looked at without spending a Gauntlet bout on it. Every
// callback is a no-op; nothing here can write anywhere.
const seat = (name, esc, hand, necro, tomes, exile = []) => ({
  name, anima: 30, maxAnima: 30, khao: 3, maxKhao: 5,
  hand, necropolis: necro, grimoire: 30, luxmoire: 12, exile,
  field: {
    esclings: esc.map((e) => e ? ({ escling: e[0], escendant: e[1], currentLyf: e[2] ?? null, soul: e[3] || [] }) : null),
    tomes: tomes.map((t) => (t ? { id: t, faceDown: false } : null)),
    curios: ["curio_wizards_cap", null],
    prophecy: "proph_space_race",
  },
});
const FAKE_ME = seat("caP'n", [
  ["escling_phantom", "spectral_tiger_beb", 2],
  ["escling_phantom", "spectral_tiger_champion", 4, ["spectral_tiger_beb", "p_spectral_tiger_1"]],
  ["escling_dragon", "chaos_dragon_lord", 6, ["chaos_dragon_beb", "chaos_dragon_champion"]],
  ["escling_time", null],
], ["z4z_beb", "tome_khao_garden", "p_spectral_tiger_2", "dg0_beb"], ["shadow_fox_beb"],
   ["tome_khao_paradise", null, "tome_lightning_blade"], ["h01_beb"]);
const FAKE_OPP = seat("Beb NPC", [
  ["escling_space", "xulan_champion", 3],
  ["escling_space", "h09_beb", 3],
  ["escling_time", "antdroid_lord", 5],
  ["escling_phantom", "inverted_serpent_beb", 4],
], ["a", "b", "c"], ["h01_beb", "lucky_dragon_beb"],
   ["tome_gambits_lucky_cat", "tome_judge_jury", "tome_omnis_corruption"]);
const NOOP = () => {};
const fakeStampsFor = (slot) => {
  const c = ESCENDANTS.find((x) => x.id === baseId(slot?.escendant || ""));
  if (!c) return null;
  const word = (axis, w) => (PRIMERAS[axis]?.[w] ? { word: w, color: "#fff", state: "ready", onPress: NOOP } : null);
  return { ilk: word("ilk", c.ilk), school: word("school", c.school) };
};

// ── THE SCRIPT ────────────────────────────────────────────────────
// `?stage=field&play=1` steps the fake duel through the events the
// bodies answer to, one every few seconds, on a loop: a summon from
// hand, an Ascend, a wound, a Lyt Attack, a ruin into the Necropolis, a
// Resurrection back out. Each step is a whole new state object, exactly
// the shape a Firebase tick hands the real board, so the detector sees
// what it would see in a match.
const clone = (o) => JSON.parse(JSON.stringify(o));
const SCRIPT = [
  { label: "the field", ms: 2500, apply: (m, o) => {} },
  { label: "SUMMON — Z4Z-Demon takes the Mind rift from hand", ms: 3200, apply: (m, o) => {
    m.hand = m.hand.filter((id) => id !== "z4z_beb");
    m.field.esclings[3] = { escling: "escling_time", escendant: "z4z_beb", currentLyf: 0, soul: [] };
  } },
  { label: "ASCEND — the Tiger Beb becomes the Champion", ms: 3200, apply: (m, o) => {
    m.field.esclings[0] = { escling: "escling_phantom", escendant: "spectral_tiger_champion", currentLyf: 4,
      soul: ["spectral_tiger_beb"] };
  } },
  { label: "SET — Rays of Hope goes down face-down", ms: 2600, apply: (m, o) => {
    m.field.tomes[1] = { id: "tome_rays_of_hope", faceDown: true };
  } },
  { label: "CAST — the set Tome is revealed; the ward performs", ms: 3000, apply: (m, o) => {
    m.field.tomes[1] = { id: "tome_rays_of_hope", faceDown: false };
  } },
  { label: "BRAND — Spectral Claws lands on the Champion's Soul", ms: 3000, apply: (m, o) => {
    m.hand = m.hand.filter((id) => id !== "p_spectral_tiger_2");
    m.field.esclings[0].soul = [...m.field.esclings[0].soul, "p_spectral_tiger_2"];
  } },
  { label: "STRIKE — the Chaos Dragon Lord swings; H-09 is wounded", ms: 3200, apply: (m, o) => {
    m.field.esclings[2].attackedThisTurn = true;
    o.field.esclings[1].currentLyf = 1;
  } },
  { label: "STRIKE — H-09 swings back; the Chaos Dragon Lord is Fractured", ms: 3200, apply: (m, o) => {
    o.field.esclings[1].attackedThisTurn = true;
    m.field.esclings[2].currentLyf = 0;
  } },
  { label: "RUIN — H-09 falls into the Necropolis", ms: 3200, apply: (m, o) => {
    o.field.esclings[1] = { escling: "escling_space", escendant: null, currentLyf: null, soul: [] };
    o.necropolis = [...o.necropolis, "h09_beb"];
  } },
  { label: "RESURRECT — the Shadow Fox Beb rises from my Necropolis", ms: 3600, apply: (m, o) => {
    m.necropolis = m.necropolis.filter((id) => id !== "shadow_fox_beb");
    m.field.esclings[3] = { escling: "escling_time", escendant: "shadow_fox_beb", currentLyf: 6, soul: [] };
  } },
  { label: "DESCENT — the Champion is ruined; the Beb stands again from the Soul, FALLEN", ms: 3600, apply: (m, o) => {
    m.field.esclings[0] = { escling: "escling_phantom", escendant: "spectral_tiger_beb", currentLyf: 2, soul: [], fallen: true };
    m.necropolis = [...m.necropolis, "spectral_tiger_champion"];
  } },
  { label: "NEXT TURN — the Fallen Beb rises; the Lord's Lyf refreshes", ms: 3000, apply: (m, o) => {
    m.field.esclings[0].fallen = false;
    m.field.esclings[2].currentLyf = 6;
    m.field.esclings[2].attackedThisTurn = false;
  } },
];
export function FieldStage() {
  const play = (() => { try { return new URLSearchParams(window.location.search).get("play"); } catch (_) { return null; } })();
  const [step, setStep] = useState(0);
  const [state, setState] = useState(() => ({ me: clone(FAKE_ME), opp: clone(FAKE_OPP) }));
  useEffect(() => {
    if (!play) return undefined;
    const t = setTimeout(() => {
      const next = (step + 1) % SCRIPT.length;
      if (next === 0) { setState({ me: clone(FAKE_ME), opp: clone(FAKE_OPP) }); setStep(0); return; }
      setState((prev) => {
        const me = clone(prev.me), opp = clone(prev.opp);
        SCRIPT[next].apply(me, opp);
        return { me, opp };
      });
      setStep(next);
    }, SCRIPT[step].ms);
    return () => clearTimeout(t);
  }, [step, play]);
  return (
    <div style={{ position: "fixed", inset: 0, background: "#07040f" }}>
      <Shatterfield3D me={state.me} opponent={state.opp} myTurn canDraw={false}
        onMySlot={NOOP} onOppSlot={NOOP} onMyTome={NOOP} onOppTome={NOOP}
        onMyNecropolis={NOOP} onOppNecropolis={NOOP} onHandCardClick={NOOP}
        onDrawGrimoire={NOOP} onDrawLuxmoire={NOOP} />
      <FieldPill me={state.me} opponent={state.opp} turn={8} seatCount={2} myTurn stampsFor={fakeStampsFor}
        onPlay={(play, at) => console.log("[pill] play", JSON.stringify(play), at.label)} />
      {play && (
        <div style={{ position: "absolute", top: 44, left: "50%", transform: "translateX(-50%)", zIndex: 40,
          color: "#f0d060", fontFamily: "serif", fontSize: 13, letterSpacing: 2, background: "rgba(0,0,6,0.7)",
          padding: "4px 12px", borderRadius: 4 }}>
          {step + 1}/{SCRIPT.length} · {SCRIPT[step].label}
        </div>
      )}
    </div>
  );
}

const PITCH_X = 1.55;
const PITCH_Z = 2.2;

function Slab({ cardId, x, z, label }) {
  return (
    <group position={[x, 0, z]}>
      <mesh position={[0, CARD.T / 2, 0]} castShadow receiveShadow>
        <boxGeometry args={[CARD.W, CARD.T, CARD.H]} />
        <meshStandardMaterial color="#2a2036" roughness={0.7} />
      </mesh>
      <group position={[0, CARD.T + 0.003, 0]}>
        <FieldBeing cardId={cardId} seed={x + z} />
      </group>
      <Html position={[0, -0.02, CARD.H / 2 + 0.18]} center style={{ pointerEvents: "none" }}>
        <div style={{
          fontFamily: "serif", fontSize: 10, letterSpacing: 1, color: "#d8c9a0",
          background: "rgba(0,0,6,0.6)", padding: "2px 6px", borderRadius: 4, whiteSpace: "nowrap",
        }}>{label}</div>
      </Html>
    </group>
  );
}

export default function BodiesStage() {
  const rows = useMemo(() => {
    const byLine = new Map();
    ESCENDANTS.forEach((c) => {
      const b = beingOf(c.id);
      if (!b) return;
      if (!byLine.has(b.lineage)) byLine.set(b.lineage, {});
      byLine.get(b.lineage)[b.tier] = c;
    });
    return Array.from(byLine.entries());
  }, []);
  const n = rows.length;
  const tiers = ["beb", "champion", "lord"];
  return (
    <div style={{ position: "fixed", inset: 0, background: "#07040f" }}>
      <div style={{ position: "absolute", top: 10, left: 12, zIndex: 5, color: "#d8c9a0", fontFamily: "serif", fontSize: 12, letterSpacing: 2 }}>
        SHIP 8 · THE REVIEW ROOM — {n} lineages × 3 tiers · stand heights {STAND_H.beb} / {STAND_H.champion} / {STAND_H.lord}
        &nbsp;· drag to orbit, wheel to zoom
      </div>
      <Canvas shadows dpr={[1, 2]} camera={{ position: [0, 7, 9], fov: 46 }} gl={{ antialias: true }}>
        <color attach="background" args={["#07040f"]} />
        <ambientLight intensity={0.55} />
        <hemisphereLight args={["#3a2f55", "#0d0a28", 0.5]} />
        <directionalLight position={[6, 12, 8]} intensity={1.1} castShadow
          shadow-mapSize-width={1024} shadow-mapSize-height={1024}
          shadow-camera-left={-12} shadow-camera-right={12}
          shadow-camera-top={12} shadow-camera-bottom={-12} shadow-camera-far={40} />
        <pointLight position={[0, 6.5, 0]} intensity={26} distance={22} decay={1.6} color="#ffd9a0" />
        <directionalLight position={[-8, 6, -8]} intensity={0.5} color="#8a70ff" />
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.001, 0]} receiveShadow>
          <planeGeometry args={[40, 40]} />
          <meshStandardMaterial color="#12081c" roughness={0.95} />
        </mesh>
        {rows.map(([lineage, byTier], i) =>
          tiers.map((tier, j) => byTier[tier] ? (
            <Slab key={byTier[tier].id} cardId={byTier[tier].id}
              x={(i - (n - 1) / 2) * PITCH_X} z={(j - 1) * PITCH_Z}
              label={byTier[tier].name} />
          ) : null))}
        <OrbitControls makeDefault target={[0, 0.4, 0]} />
      </Canvas>
    </div>
  );
}
